import React, { useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { saveCommunityTraining, uploadLearningFile } from '../services/learningCenterService';
import { CommunityTraining, formatTrainingDuration, LearningTraining } from '../types';
import LessonFeedPlayer, { LessonFeedItem } from './LessonFeedPlayer';
import TrainingCardMedia from './TrainingCardMedia';

const asFeedTraining = (training: CommunityTraining): LearningTraining => ({
  id: training.id,
  title: training.title,
  description: training.description,
  writtenContent: training.writtenContent,
  video: training.video,
  documents: training.documents || [],
  tags: [],
  searchKeywords: [],
  audience: ['teachers'],
  completionType: 'acknowledgment',
  published: true,
  communityEducation: true,
});

const DiscoverTile: React.FC<{ item: LessonFeedItem; onOpen: () => void }> = ({ item, onOpen }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { training } = item;
  return (
    <button
      type="button"
      className="community-discover__tile"
      onClick={onOpen}
      onMouseEnter={() => { videoRef.current?.play().catch(() => undefined); }}
      onMouseLeave={() => { const video = videoRef.current; if (video) { video.pause(); video.currentTime = 0.1; } }}
    >
      {training.video?.url ? (
        <video ref={videoRef} src={`${training.video.url}#t=0.1`} muted loop playsInline preload="metadata" />
      ) : (
        <TrainingCardMedia training={training} />
      )}
      <span className="community-discover__tile-copy">
        <small>{item.moduleTitle || formatTrainingDuration(training)}</small>
        <strong>{training.title}</strong>
      </span>
    </button>
  );
};

const CommunityDiscover: React.FC<{ libraryTrainings: LearningTraining[]; communityTrainings: CommunityTraining[] }> = ({
  libraryTrainings,
  communityTrainings,
}) => {
  const { currentUser, userData } = useAuth();
  const [search, setSearch] = useState('');
  const [feedStartId, setFeedStartId] = useState('');
  const [showSubmit, setShowSubmit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [submission, setSubmission] = useState({ title: '', description: '', writtenContent: '' });
  const [submissionVideo, setSubmissionVideo] = useState<File | null>(null);
  const [submissionDocuments, setSubmissionDocuments] = useState<File[]>([]);

  const items = useMemo<LessonFeedItem[]>(() => {
    const needle = search.trim().toLowerCase();
    const matches = (value: string) => !needle || value.toLowerCase().includes(needle);
    const library = libraryTrainings.filter(training => matches([training.title, training.description, training.learningObjective, ...training.tags, ...training.searchKeywords].join(' '))).map(training => ({
      training,
      stepId: training.id,
      feedKey: `library:${training.id}`,
      moduleTitle: formatTrainingDuration(training),
      complete: false,
      commentPath: { collection: 'learningTrainings' as const, id: training.id },
    }));
    const shared = communityTrainings.filter(training => matches([training.title, training.description, training.authorName].join(' '))).map(training => ({
      training: asFeedTraining(training),
      stepId: training.id,
      feedKey: `community:${training.id}`,
      moduleTitle: `Shared by ${training.authorName}`,
      complete: false,
      commentPath: { collection: 'communityTrainings' as const, id: training.id },
    }));
    return [...library, ...shared];
  }, [communityTrainings, libraryTrainings, search]);

  const submit = async () => {
    if (!currentUser?.uid || !submission.title.trim()) return;
    setBusy(true);
    try {
      const base = `learning/community/${currentUser.uid}`;
      const video = submissionVideo ? await uploadLearningFile(base, submissionVideo) : null;
      const documents = await Promise.all(submissionDocuments.map(file => uploadLearningFile(base, file)));
      await saveCommunityTraining({
        ...submission, video, documents,
        authorId: currentUser.uid,
        authorName: String(userData?.displayName || currentUser.displayName || 'Overture teacher'),
        status: 'pending',
      });
      setSubmission({ title: '', description: '', writtenContent: '' });
      setSubmissionVideo(null); setSubmissionDocuments([]); setShowSubmit(false);
      setMessage('Your lesson was submitted for admin approval.');
    } catch (error: any) {
      setMessage(error?.message || 'Could not submit lesson.');
    } finally { setBusy(false); }
  };

  return (
    <section className="learning-stage community-education">
      <div className="learning-stage__heading">
        <div>
          <small>For you</small>
          <h2>Community</h2>
          <p>Swipe through teacher-shared lessons, or browse the discover grid.</p>
        </div>
        <button className="community-education__submit-button" onClick={() => setShowSubmit(value => !value)}>Share a lesson</button>
      </div>
      {message && <div className="learning-center__message">{message}</div>}
      <input className="community-discover__search" type="search" placeholder="Search lessons, tags, or teachers" value={search} onChange={event => setSearch(event.target.value)} />
      {showSubmit && (
        <div className="community-submission">
          <h3>Submit a community lesson</h3>
          <p>Submissions are reviewed by an admin before appearing here.</p>
          <input placeholder="Lesson title" value={submission.title} onChange={e => setSubmission(v => ({ ...v, title: e.target.value }))} />
          <textarea rows={2} placeholder="Short description" value={submission.description} onChange={e => setSubmission(v => ({ ...v, description: e.target.value }))} />
          <textarea rows={7} placeholder="Written lesson (optional)" value={submission.writtenContent} onChange={e => setSubmission(v => ({ ...v, writtenContent: e.target.value }))} />
          <label>Video (optional)<input type="file" accept="video/*" onChange={e => setSubmissionVideo(e.target.files?.[0] || null)} /></label>
          <label>Supporting documents<input type="file" multiple accept="application/pdf,image/*,.doc,.docx" onChange={e => setSubmissionDocuments(Array.from(e.target.files || []))} /></label>
          <button disabled={busy || !submission.title.trim()} onClick={() => void submit()}>{busy ? 'Submitting…' : 'Submit for approval'}</button>
        </div>
      )}
      <div className="community-discover">
        {items.map(item => <DiscoverTile key={item.feedKey} item={item} onOpen={() => setFeedStartId(item.feedKey || item.training.id)} />)}
        {!items.length && <p className="community-education__empty">No matching lessons.</p>}
      </div>
      {feedStartId && items.length > 0 && (
        <LessonFeedPlayer items={items} startTrainingId={feedStartId} onClose={() => setFeedStartId('')} />
      )}
    </section>
  );
};

export default CommunityDiscover;
